import { CSSProperties, ReactNode, useEffect, useRef, useState } from "react";
import style from "./dropdown-picker.module.css";
import useOutsideRef from "frontend/utils/click-outside-handler";
import cn from "classnames";
import OptionsPicker from "./options-picker";
import { MagnifyingGlassIcon } from "frontend/ui-components/picker/magnifying-glass-icon";
import { CloseIcon } from "frontend/icons/close";
import { getPathPrefix } from "../../utils/getPathPrefix";
import { useFloating, autoUpdate, offset, shift, size, FloatingPortal } from "@floating-ui/react";
import { PortalID } from "frontend/modal/portal-types";

const MIN_HEIGHT = 125;

export enum DropdownPickerStyle {
  Light = "light",
  Dark = "dark",
}

type DropdownPickerProps<T> = {
  testId?: string;
  options: T[];
  isSelected: (option: T) => boolean;
  titles: (option: T) => string;
  enabled: boolean;
  onChange: (option: T) => void;
  optionRenderer?: ({ option, selected, index }: { option: T; selected: boolean; index: number }) => {
    element: ReactNode;
    enabled: boolean;
    customStyle?: CSSProperties;
    handlesSelectedState?: boolean;
    customIcon?: string;
    wrapWithContainer?: boolean;
  };
  iconRenderer?: () => ReactNode;
  noOptionsRenderer?: () => ReactNode;
  selectionHeaderRenderer?: ({ selectedOptions }: { selectedOptions: T[] }) => ReactNode;
  multiValue?: boolean;
  placeholder?: string;
  scrollToSelected?: boolean;
  optionsPickerStyle?: CSSProperties;
  pickerCustomIcon?: string;
  pickerStyle?: DropdownPickerStyle;
  pickerCustomStyle?: CSSProperties;
  optionsContainerStyle?: CSSProperties;
  enableSearch?: boolean;
  onSearch?: (search: string) => void;
  loading?: boolean;
  styles?: {
    pickerActiveStyle?: CSSProperties;
    optionsPickerActiveStyle?: CSSProperties;
    searchContainer?: string;
    optionListClassName?: string;
    optionClassName?: string;
    optionTitleClassName?: string;
    placeHolderClassName?: string;
    pickerParent?: string;
    selectedOption?: string;
    rounded?: boolean;
  };
  width?: number;
  showClearButton?: boolean;
  onClear?: () => void;
  searchPlaceholder?: string;
  onFetchMore?: () => void;
  loadingMore?: boolean;
  noResultsText?: (searchValue: string) => ReactNode;
  includeOverlay?: boolean;
  useFloatingDropdown?: boolean;
  portalId?: string;
};

export default function DropdownPicker<T>({
  testId,
  options,
  isSelected,
  titles,
  enabled,
  onChange,
  optionRenderer,
  iconRenderer,
  noOptionsRenderer,
  selectionHeaderRenderer,
  multiValue = false,
  placeholder,
  scrollToSelected,
  optionsPickerStyle,
  pickerCustomIcon,
  pickerStyle,
  pickerCustomStyle,
  optionsContainerStyle,
  enableSearch = false,
  onSearch,
  loading = false,
  styles,
  width,
  showClearButton = false,
  onClear,
  searchPlaceholder = "Search",
  onFetchMore,
  loadingMore = false,
  noResultsText = (_: string) => `No search results found`,
  includeOverlay = false,
  useFloatingDropdown = false,
  portalId = PortalID.Floaters,
}: DropdownPickerProps<T>) {
  const [showPicker, setShowPicker] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [showAbove, setShowAbove] = useState(false);

  const { refs: floatingRefs, floatingStyles } = useFloating({
    open: showPicker && useFloatingDropdown,
    placement: "bottom-start",
    middleware: [
      offset(4),
      {
        name: "conditionalFlip",
        async fn(state) {
          const { y, rects, placement } = state;
          const viewportHeight = window.innerHeight;
          const availableBelow = viewportHeight - (y + rects.reference.height + 4);

          if (availableBelow < MIN_HEIGHT && placement === "bottom-start") {
            return {
              data: { flip: true },
              reset: {
                placement: "top-start",
              },
            };
          }

          return {};
        },
      },
      shift({ padding: 8 }),
      size({
        apply({ availableHeight, elements }) {
          const maxHeight = Math.max(MIN_HEIGHT, availableHeight - 8);
          Object.assign(elements.floating.style, {
            maxHeight: `${maxHeight}px`,
          });
        },
        padding: 8,
      }),
    ],
    whileElementsMounted: autoUpdate,
  });

  const ref = useRef<HTMLDivElement>(null);
  const optionsContainerRef = useRef<HTMLDivElement>(null);
  const optionsListRef = useRef<HTMLDivElement>(null);
  useOutsideRef(ref, () => setShowPicker(false), { disableDetection: useFloatingDropdown });

  useEffect(() => {
    if (!useFloatingDropdown || !showPicker) return;

    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as Node;
      const refElement = ref.current;
      const floatingElement = floatingRefs.floating.current;

      if (refElement && !refElement.contains(target) && floatingElement && !floatingElement.contains(target)) {
        setShowPicker(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [useFloatingDropdown, showPicker, floatingRefs.floating]);

  useEffect(() => {
    if (!enableSearch) {
      return;
    }
    if (onSearch) {
      onSearch(searchValue);
    }
  }, [searchValue, enableSearch, onSearch]);

  useEffect(() => {
    if (useFloatingDropdown && ref.current) {
      floatingRefs.setReference(ref.current);
    }
  }, [useFloatingDropdown, floatingRefs]);

  useEffect(() => {
    if (useFloatingDropdown) return;
    if (showPicker && ref.current && optionsContainerRef.current) {
      const fieldRect = ref.current.getBoundingClientRect();
      const optionsHeight = optionsContainerRef.current.offsetHeight;
      const spaceBelow = window.innerHeight - fieldRect.bottom;

      const hasSpaceOnlyAbove = spaceBelow < optionsHeight && fieldRect.top > optionsHeight;
      setShowAbove(hasSpaceOnlyAbove);
    }
  }, [showPicker, options, useFloatingDropdown]);

  useEffect(() => {
    if (!showPicker || !onFetchMore || loadingMore) return;

    const handleScroll = () => {
      if (!optionsListRef.current) return;

      const { scrollTop, scrollHeight, clientHeight } = optionsListRef.current;
      const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;

      // Trigger when scrolled to 90% of the list
      if (scrollPercentage > 0.9) {
        onFetchMore();
      }
    };

    const optionsList = optionsListRef.current;
    if (optionsList) {
      optionsList.addEventListener("scroll", handleScroll);
      // Check immediately in case the list is already short enough
      handleScroll();
    }

    return () => {
      if (optionsList) {
        optionsList.removeEventListener("scroll", handleScroll);
      }
    };
  }, [showPicker, onFetchMore, loadingMore]);

  function onChooseOption(option: T) {
    setSearchValue("");
    onChange(option);
    if (!multiValue) {
      setShowPicker(false);
    }
  }

  const handleClearSelection = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent dropdown from opening
    if (onClear) {
      onClear();
    }
  };

  function renderOptions() {
    const filteredOptions = options.filter((option) => {
      if (!searchValue) {
        return true;
      }
      return titles(option).toLowerCase().includes(searchValue.toLowerCase());
    });

    if (!loading && !loadingMore && searchValue && filteredOptions.length === 0) {
      return <div className={style.noResults}>{noResultsText?.(searchValue) ?? "No search results found"}</div>;
    }

    return filteredOptions.map((option, index) => {
      const selected = isSelected(option);
      const {
        element,
        enabled,
        customStyle = {},
        handlesSelectedState = false,
        wrapWithContainer = true,
      } = optionRenderer ? optionRenderer({ option, selected, index }) : { element: null, enabled: true };
      const title = element ?? titles(option);

      if (!wrapWithContainer) {
        return (
          <div
            id={`${option}`}
            key={index}
            onClick={(e) => {
              e.stopPropagation();
              enabled && onChooseOption(option);
            }}
          >
            {element}
          </div>
        );
      }

      return (
        <div
          id={`${option}`}
          key={index}
          className={cn(style.option, styles?.optionClassName ?? "", {
            [style.selected]: selected && !handlesSelectedState,
            [style.disabled]: !enabled,
            [style.dark]: pickerStyle === DropdownPickerStyle.Dark,
            [style.rounded]: styles?.rounded,
          })}
          data-selected={selected}
          onClick={(e) => {
            e.stopPropagation();
            enabled && onChooseOption(option);
          }}
          style={customStyle}
        >
          <div className={cn(style.optionTitle, styles?.optionTitleClassName ?? "")}>{title}</div>
          {!handlesSelectedState && selected && !styles?.rounded && (
            <img src={getPathPrefix("/images/feature-check-i.svg")} alt="feature-check-i" />
          )}
        </div>
      );
    });
  }

  let selectedOptions = options.filter(isSelected);
  // if no placeholder, select first option by default
  if (!placeholder && options.length > 0 && selectedOptions.length === 0) {
    selectedOptions = [options[0]];
  }

  const hasSelection = selectedOptions.length > 0;

  function renderSelectedOptions() {
    if (placeholder && selectedOptions.length === 0) {
      return (
        <div
          style={optionsPickerStyle}
          className={cn(styles?.placeHolderClassName ?? "", style.placeholderTitle, {
            [style.dark]: pickerStyle === DropdownPickerStyle.Dark,
          })}
          title={placeholder}
        >
          {placeholder}
        </div>
      );
    }
    const { element, customIcon } = optionRenderer
      ? optionRenderer({ option: selectedOptions[0], selected: false, index: 0 })
      : { element: null, customIcon: null };
    const selectedTitles = element ?? selectedOptions.map(titles).join(", ");

    if (selectionHeaderRenderer) {
      return selectionHeaderRenderer({ selectedOptions });
    }

    return (
      <div
        style={{ ...optionsPickerStyle, ...pickerCustomStyle }}
        className={style.selectedTitle}
        title={typeof selectedTitles === "string" ? selectedTitles : titles(selectedOptions[0])}
      >
        {customIcon && <img className={style.customIcon} src={customIcon} alt="custom-icon" />}
        {selectedTitles}
      </div>
    );
  }

  function renderIcon() {
    if (iconRenderer) {
      return iconRenderer?.();
    }
    if (!enabled) {
      return null;
    }
    if (pickerCustomIcon) {
      return <img src={pickerCustomIcon} alt="custom-icon" />;
    }
    return (
      <img
        src={getPathPrefix("/images/chevron.svg")}
        className={cn(style.chevron, {
          [style.dark]: pickerStyle === DropdownPickerStyle.Dark,
        })}
        alt="chevron"
      />
    );
  }

  const shouldShowPicker = (options.length > 0 || enableSearch) && enabled && showPicker;

  const renderOptionsPickerContent = () => {
    const content = (
      <OptionsPicker
        className={cn(style.optionsContainer, {
          [style.positionAbove]: showAbove && !useFloatingDropdown,
          [style.rounded]: styles?.rounded,
        })}
        containerStyle={{
          ...optionsPickerStyle,
          ...optionsContainerStyle,
          ...styles?.optionsPickerActiveStyle,
          ...(useFloatingDropdown
            ? {
                ...floatingStyles,
              }
            : undefined),
        }}
        scrollToSelected={scrollToSelected && selectedOptions.length > 0 ? titles(selectedOptions[0]) : undefined}
        pickerStyle={pickerStyle}
        ref={useFloatingDropdown ? floatingRefs.setFloating : optionsContainerRef}
        {...(useFloatingDropdown ? { "data-floating-dropdown": "true" } : {})}
      >
        {enableSearch && (
          <div className={cn(style.searchContainer, styles?.searchContainer ?? "")}>
            <MagnifyingGlassIcon />
            <input
              type="text"
              value={searchValue}
              placeholder={searchPlaceholder}
              autoFocus
              onChange={(e) => setSearchValue(e.target.value.toLowerCase())}
            />
          </div>
        )}
        <div className={cn(style.optionsList, styles?.optionListClassName ?? "")} ref={optionsListRef}>
          {renderOptions()}
          {(false || loadingMore) && <div className={style.loadingMore}>Loading more options...</div>}
          {includeOverlay && <div className={style.overlay} />}
        </div>
      </OptionsPicker>
    );

    return useFloatingDropdown ? <FloatingPortal id={portalId}>{content}</FloatingPortal> : content;
  };

  return (
    <div className={cn(style.picker, styles?.pickerParent)} ref={ref} style={{ width }} data-testid={testId}>
      {options.length === 0 && noOptionsRenderer && noOptionsRenderer()}
      {(options.length > 0 || !noOptionsRenderer) && (
        <div
          className={cn(style.selectedOption, styles?.selectedOption, {
            [style.disabled]: !enabled,
            [style.dark]: pickerStyle === DropdownPickerStyle.Dark,
            [style.rounded]: styles?.rounded,
          })}
          onClick={() => {
            setShowPicker(!showPicker);
          }}
          style={{ ...pickerCustomStyle, ...(shouldShowPicker ? styles?.pickerActiveStyle : {}) }}
        >
          {renderSelectedOptions()}
          {hasSelection && showClearButton && onClear && (
            <button
              className={cn(style.clearButton, {
                [style.dark]: pickerStyle === DropdownPickerStyle.Dark,
              })}
              onClick={handleClearSelection}
              title="Clear selection"
              type="button"
            >
              <CloseIcon color={pickerStyle === DropdownPickerStyle.Dark ? "#ffffff" : "#848199"} />
            </button>
          )}
          {renderIcon()}
        </div>
      )}
      {shouldShowPicker && renderOptionsPickerContent()}
    </div>
  );
}
